import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { IssueTypeIdentifier, IssueTypePriority, IssueTypeStatus, Stored, User } from "@overmap-ai/common"
import { type Field, FieldSchema } from "@overmap-ai/forms"
import { memo, type ReactElement, useCallback, useMemo } from "react"

import { AssigneeFilterDef } from "../../filtering/defs/AssigneeFilterDef"
import { CreatedByFilterDef } from "../../filtering/defs/CreatedByFilterDef"
import { DueDateFilterDef } from "../../filtering/defs/DueDateFilterDef"
import { IdentifierFilterDef } from "../../filtering/defs/IdentifierFilterDef"
import { IssuePriorityFilterDef } from "../../filtering/defs/IssuePriorityFilterDef"
import { IssueTypeStatusFilterDef } from "../../filtering/defs/IssueStatusFilterDef"
import { IssueTitleFilterDef } from "../../filtering/defs/IssueTitleFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { IssueData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"
import { IdentifierGroup } from "../MenuGroups/IdentifierGroup"

export interface IssueDataFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	assignees: User[]
	issueTypeIdentifiers: Stored<IssueTypeIdentifier>[]
	issueTypePriorities: Stored<IssueTypePriority>[]
	issueTypeStatuses: Stored<IssueTypeStatus>[]
	issueTypeFieldSchema: FieldSchema
	users: User[]
	filterManager: FilterManager<IssueData>
}

export const IssueDataFilterMenu = memo<IssueDataFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		filterManager,
		assignees,
		issueTypeIdentifiers,
		issueTypePriorities,
		issueTypeStatuses,
		issueTypeFieldSchema,
		users,
		...rest
	} = props

	const handleSelectIssueTitle = useCallback(() => {
		filterManager.addFilter(new IssueTitleFilterDef().createFilter())
	}, [filterManager])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectCreatedBy = useCallback(() => {
		filterManager.addFilter(new CreatedByFilterDef(users).createFilter())
	}, [filterManager, users])

	const handleSelectIssuePriority = useCallback(() => {
		filterManager.addFilter(new IssuePriorityFilterDef(issueTypePriorities).createFilter())
	}, [filterManager, issueTypePriorities])

	const handleSelectIssueStatus = useCallback(() => {
		filterManager.addFilter(new IssueTypeStatusFilterDef(issueTypeStatuses).createFilter())
	}, [filterManager, issueTypeStatuses])

	const handleSelectAssignee = useCallback(() => {
		filterManager.addFilter(new AssigneeFilterDef(assignees).createFilter())
	}, [assignees, filterManager])

	const handleSelectDueDate = useCallback(() => {
		filterManager.addFilter(new DueDateFilterDef().createFilter())
	}, [filterManager])

	const handleIdentifierSelect = useCallback(
		(identifier: Stored<IssueTypeIdentifier>) => {
			filterManager.addFilter(new IdentifierFilterDef(identifier).createFilter())
		},
		[filterManager],
	)

	const handleFieldSelect = useCallback(
		(field: Field) => {
			filterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[filterManager],
	)

	const supportedFields = useMemo(() => issueTypeFieldSchema?.getFields(), [issueTypeFieldSchema])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectIssueTitle}>
						<LuIcon icon={modelIconMapping.text} />
						Title
					</Menu.Item>
					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>
					<Menu.Item onSelect={handleSelectCreatedBy}>
						<LuIcon icon={modelIconMapping.creator} />
						Created by
					</Menu.Item>
					<Menu.Item onSelect={handleSelectIssuePriority}>
						<LuIcon icon={modelIconMapping.priority} />
						Priority
					</Menu.Item>
					<Menu.Item onSelect={handleSelectIssueStatus}>
						<LuIcon icon={modelIconMapping.status} />
						Status
					</Menu.Item>
					<Menu.Item onSelect={handleSelectAssignee}>
						<LuIcon icon={modelIconMapping.assignee} />
						Assigned to
					</Menu.Item>
					<Menu.Item onSelect={handleSelectDueDate}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Due date
					</Menu.Item>

					{issueTypeIdentifiers.length > 0 && (
						<>
							<Menu.Label>Identifiers</Menu.Label>
							<IdentifierGroup
								identifiers={issueTypeIdentifiers}
								onIdentifierSelect={handleIdentifierSelect}
							/>
						</>
					)}

					{supportedFields.length > 0 && (
						<>
							<Menu.Label>Fields</Menu.Label>
							<FieldGroup fields={supportedFields} onFieldSelect={handleFieldSelect} />
						</>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
IssueDataFilterMenu.displayName = "IssueDataFilterMenu"
